import React, { useState } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useTheme } from "@/components/ThemeProvider";
import { getIcon } from "@/data/iconMap";
import { Sun, Moon, X, List, Maximize, RefreshCw, Settings } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export const FloatingMenu = ({ currentShowcaseIdx, showcases = [], groupedData = [], onGoToShowcase, onRefresh, onToggleFullscreen, menuOpen, setMenuOpen }) => {
  const { theme, toggleTheme } = useTheme();
  const [fabOpen, setFabOpen] = useState(false);

  return (
    <>
      {/* Floating Action Button group - bottom right */}
      <div className="fixed bottom-5 right-5 z-40 flex flex-col-reverse items-center gap-2">
        {/* Main FAB toggle - gear icon */}
        <button
          onClick={() => setFabOpen(!fabOpen)}
          className={cn(
            "flex items-center justify-center w-12 h-12 rounded-full shadow-lg-custom border transition-all duration-300",
            fabOpen
              ? "bg-primary text-primary-foreground border-primary/50"
              : "bg-card text-foreground border-border/40 hover:bg-muted"
          )}
          aria-label="Toggle floating menu"
        >
          <Settings className={cn("h-5 w-5 transition-transform duration-500", fabOpen && "rotate-90")} />
        </button>

        {/* Expanded items */}
        <AnimatePresence>
          {fabOpen && (
            <>
              {/* Page counter badge */}
              {currentShowcaseIdx >= 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 10, scale: 0.8 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 10, scale: 0.8 }}
                  transition={{ delay: 0.2 }}
                >
                  <Badge variant="number" className="px-3 py-1.5 text-xs shadow-md-custom">
                    {String(currentShowcaseIdx + 1).padStart(2, '0')} / {String(showcases.length).padStart(2, '0')}
                  </Badge>
                </motion.div>
              )}

              {/* Fullscreen toggle */}
              <motion.button
                initial={{ opacity: 0, y: 10, scale: 0.8 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 10, scale: 0.8 }}
                transition={{ delay: 0.2 }}
                onClick={() => {
                  onToggleFullscreen();
                  setFabOpen(false);
                }}
                className="flex items-center justify-center w-10 h-10 rounded-full bg-card text-foreground border border-border/40 shadow-md-custom hover:bg-muted transition-colors duration-200"
                aria-label="Toggle fullscreen"
              >
                <Maximize className="h-4 w-4" />
              </motion.button>

              {/* Refresh (F5) */}
              <motion.button
                initial={{ opacity: 0, y: 10, scale: 0.8 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 10, scale: 0.8 }}
                transition={{ delay: 0.15 }}
                onClick={() => {
                  onRefresh();
                  setFabOpen(false);
                }}
                className="flex items-center justify-center w-10 h-10 rounded-full bg-card text-foreground border border-border/40 shadow-md-custom hover:bg-muted transition-colors duration-200"
                aria-label="Refresh"
              >
                <RefreshCw className="h-4 w-4" />
              </motion.button>

              {/* Theme toggle */}
              <motion.button
                initial={{ opacity: 0, y: 10, scale: 0.8 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 10, scale: 0.8 }}
                transition={{ delay: 0.1 }}
                onClick={toggleTheme}
                className="flex items-center justify-center w-10 h-10 rounded-full bg-card text-foreground border border-border/40 shadow-md-custom hover:bg-muted transition-colors duration-200"
                aria-label="Toggle theme"
              >
                {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </motion.button>

              {/* Showcase list menu */}
              <motion.button
                initial={{ opacity: 0, y: 10, scale: 0.8 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 10, scale: 0.8 }}
                transition={{ delay: 0.05 }}
                onClick={() => {
                  setMenuOpen(true);
                  setFabOpen(false);
                }}
                className="flex items-center justify-center w-10 h-10 rounded-full bg-card text-foreground border border-border/40 shadow-md-custom hover:bg-muted transition-colors duration-200"
                aria-label="Open showcase list"
              >
                <List className="h-4 w-4" />
              </motion.button>
            </>
          )}
        </AnimatePresence>
      </div>

      {/* Side Menu Panel */}
      <AnimatePresence>
        {menuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-50 bg-background/60 backdrop-blur-sm"
              onClick={() => setMenuOpen(false)}
            />
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 300 }}
              className="fixed right-0 top-0 bottom-0 z-50 w-80 md:w-96 bg-card border-l border-border/30 overflow-y-auto"
            >
              <div className="p-6 pt-8">
                <div className="flex items-center justify-between mb-6">
                  <p className="text-xs font-medium tracking-widest uppercase text-muted-foreground">
                    Product Showcases
                  </p>
                  <button
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-muted transition-colors duration-200"
                    aria-label="Close menu"
                  >
                    <X className="h-4 w-4 text-muted-foreground" />
                  </button>
                </div>
                <div className="space-y-4">
                  {groupedData.length > 0 ? groupedData.map(({ category, items }) => {
                    // Compute flat index offset for this category group
                    let flatStartIdx = 0;
                    for (const g of groupedData) {
                      if (g.category.key === category.key) break;
                      flatStartIdx += g.items.length;
                    }
                    return (
                      <div key={category.key}>
                        <p className="text-[10px] font-semibold tracking-widest uppercase text-muted-foreground/70 px-4 mb-1.5">
                          {category.label}. {category.title}
                        </p>
                        <div className="space-y-1">
                          {items.map((showcase, localIdx) => {
                            const globalIdx = flatStartIdx + localIdx;
                            const Icon = getIcon(showcase.icon);
                            const isActive = currentShowcaseIdx === globalIdx;
                            return (
                              <button
                                key={showcase.id}
                                onClick={() => onGoToShowcase(globalIdx)}
                                className={cn(
                                  "w-full flex items-center gap-4 px-4 py-3 rounded-lg text-left transition-colors duration-200",
                                  isActive
                                    ? "bg-primary/10 text-primary border border-primary/20"
                                    : "text-muted-foreground hover:text-foreground hover:bg-muted/30"
                                )}
                              >
                                <span className={cn(
                                  "flex items-center justify-center w-8 h-8 rounded-lg text-xs font-heading font-semibold",
                                  isActive ? "bg-primary/20 text-primary" : "bg-muted/50 text-muted-foreground"
                                )}>
                                  {String(globalIdx + 1).padStart(2, '0')}
                                </span>
                                <div className="flex-1 min-w-0">
                                  <p className={cn(
                                    "text-sm font-medium font-heading truncate",
                                    isActive ? "text-primary" : ""
                                  )}>
                                    {showcase.title}
                                  </p>
                                </div>
                                <Icon className={cn(
                                  "h-4 w-4 shrink-0",
                                  isActive ? "text-primary" : "text-muted-foreground/50"
                                )} />
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  }) : showcases.map((showcase, index) => {
                    const Icon = getIcon(showcase.icon);
                    const isActive = currentShowcaseIdx === index;
                    return (
                      <button
                        key={showcase.id}
                        onClick={() => onGoToShowcase(index)}
                        className={cn(
                          "w-full flex items-center gap-4 px-4 py-3 rounded-lg text-left transition-colors duration-200",
                          isActive
                            ? "bg-primary/10 text-primary border border-primary/20"
                            : "text-muted-foreground hover:text-foreground hover:bg-muted/30"
                        )}
                      >
                        <span className={cn(
                          "flex items-center justify-center w-8 h-8 rounded-lg text-xs font-heading font-semibold",
                          isActive ? "bg-primary/20 text-primary" : "bg-muted/50 text-muted-foreground"
                        )}>
                          {String(index + 1).padStart(2, '0')}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className={cn(
                            "text-sm font-medium font-heading truncate",
                            isActive ? "text-primary" : ""
                          )}>
                            {showcase.title}
                          </p>
                        </div>
                        <Icon className={cn(
                          "h-4 w-4 shrink-0",
                          isActive ? "text-primary" : "text-muted-foreground/50"
                        )} />
                      </button>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};
